import React, { useEffect, useState } from 'react';
import { Box, Typography, Slider, Grid, Paper, Tooltip } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';

interface Weights {
    velocity: number;
    transport: number;
    holding: number;
    capacity: number;
}

interface WeightSlidersProps {
    initialWeights: Weights;
    onChange: (weights: Weights) => void;
}

const WeightSliders: React.FC<WeightSlidersProps> = ({ initialWeights, onChange }) => {
    const [weights, setWeights] = useState<Weights>(initialWeights);

    const handleSliderChange = (field: keyof Weights, newValue: number) => {
        const oldValue = weights[field];
        const diff = newValue - oldValue;

        // Calculate sum of other weights
        const otherKeys = (Object.keys(weights) as (keyof Weights)[]).filter(k => k !== field);
        const otherSum = otherKeys.reduce((sum, k) => sum + weights[k], 0);

        const newWeights = { ...weights };
        newWeights[field] = newValue;

        if (otherSum > 0) {
            // Proportional adjustment
            otherKeys.forEach(k => {
                const ratio = weights[k] / otherSum;
                newWeights[k] = Math.max(0, weights[k] - diff * ratio);
            });
        } else {
            // If others are 0, distribute equally
            otherKeys.forEach(k => {
                newWeights[k] = Math.max(0, (1 - newValue) / otherKeys.length);
            });
        }

        // Final normalization to ensure sum is exactly 1.0
        const finalSum = Object.values(newWeights).reduce((a, b) => a + b, 0);
        if (Math.abs(finalSum - 1.0) > 0.001) {
            const scale = 1.0 / finalSum;
            Object.keys(newWeights).forEach(k => {
                (newWeights as any)[k] *= scale;
            });
        }

        // Round to 3 decimal places to avoid floating point issues in logs/API
        const roundedWeights = { ...newWeights };
        (Object.keys(roundedWeights) as (keyof Weights)[]).forEach(k => {
            roundedWeights[k] = Math.round(roundedWeights[k] * 1000) / 1000;
        });

        setWeights(roundedWeights);
        onChange(roundedWeights);
    };

    const getLabel = (key: keyof Weights) => {
        switch (key) {
            case 'velocity': return 'Sales Velocity';
            case 'transport': return 'Transport Cost';
            case 'holding': return 'Holding Cost';
            case 'capacity': return 'Available Capacity';
            default: return key;
        }
    };

    const getDescription = (key: keyof Weights) => {
        switch (key) {
            case 'velocity': return 'Prioritize regions where vehicles sell fastest to reduce days-on-lot.';
            case 'transport': return 'Minimize shipping expenses from plant to regional ports/hubs.';
            case 'holding': return 'Minimize projected interest and storage costs for inventory.';
            case 'capacity': return 'Prioritize regions with the most physical space available for new inventory.';
            default: return '';
        }
    };

    return (
        <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1a1a2e', mb: 3 }}>
                Optimization Multipliers
            </Typography>

            <Grid container spacing={4}>
                {(Object.keys(weights) as (keyof Weights)[]).map((key) => (
                    <Grid item xs={12} key={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, color: '#455a64' }}>
                                {getLabel(key)}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', minWidth: 40, textAlign: 'right' }}>
                                {(weights[key] * 100).toFixed(0)}%
                            </Typography>
                            <Tooltip title={getDescription(key)} arrow>
                                <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                        </Box>
                        <Slider
                            value={weights[key]}
                            min={0}
                            max={1}
                            step={0.01}
                            onChange={(_, val) => handleSliderChange(key, val as number)}
                            sx={{
                                color: 'primary.main',
                                height: 4,
                                '& .MuiSlider-thumb': {
                                    width: 14,
                                    height: 14,
                                    backgroundColor: '#fff',
                                    border: '2px solid currentColor',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                                        boxShadow: 'inherit',
                                    },
                                    '&:before': {
                                        display: 'none',
                                    },
                                },
                                '& .MuiSlider-track': {
                                    border: 'none',
                                    height: 4,
                                },
                                '& .MuiSlider-rail': {
                                    opacity: 0.5,
                                    backgroundColor: '#bfbfbf',
                                    height: 4,
                                },
                            }}
                        />
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #cfd8dc' }}>
                <Typography variant="caption" color="text.secondary">
                    Normalized Sum: {(Object.values(weights).reduce((a, b) => a + b, 0) * 100).toFixed(0)}%
                </Typography>
            </Box>
        </Paper>
    );
};

export default WeightSliders;
